import { useRef, useState } from 'react';

type PlayableSegment = {
  id: number;
  startSeconds: number;
  endSeconds: number;
};

type SegmentPlayback = {
  /** Attach to a single mounted `<audio>` element; playback is driven off it, not `new Audio()`. */
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: (id: number) => boolean;
  /** Playback position within the given segment, 0..1 (0 when it's not the active segment). */
  progressFor: (id: number) => number;
  toggle: (segment: PlayableSegment) => void;
  handleTimeUpdate: () => void;
  handleEnded: () => void;
  handleError: () => void;
};

// A short tail past the cited end so auto-pause doesn't clip the last word when the
// transcription's end marker lands slightly early.
const AUTO_PAUSE_TAIL_SECONDS = 0.3;

/**
 * Plays a segment's [start, end] range on one shared, mounted `<audio>` element: seeks to the
 * start, pauses just past the end, and reports progress. The consumer mounts
 * `<audio ref={audioRef} src={url} … />` and wires the handlers. Only one segment plays at a
 * time; toggling the active segment pauses it.
 */
export function useSegmentPlayback(): SegmentPlayback {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const activeSegmentRef = useRef<PlayableSegment | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);

  const stop = () => {
    activeSegmentRef.current = null;
    setActiveId(null);
    setProgress(0);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    const segment = activeSegmentRef.current;
    if (!audio || !segment) return;
    if (audio.currentTime >= segment.endSeconds + AUTO_PAUSE_TAIL_SECONDS) {
      audio.pause();
      stop();
      return;
    }
    const span = segment.endSeconds - segment.startSeconds;
    const elapsed = audio.currentTime - segment.startSeconds;
    setProgress(span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : 0);
  };

  const handleEnded = () => stop();

  const handleError = () => {
    audioRef.current?.pause();
    stop();
  };

  const toggle = (segment: PlayableSegment) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Decide on the ref, which is always current — `activeId` state can lag a just-committed reset.
    if (activeSegmentRef.current?.id === segment.id) {
      audio.pause();
      stop();
      return;
    }

    audio.pause();
    activeSegmentRef.current = segment;
    audio.currentTime = segment.startSeconds;
    setActiveId(segment.id);
    setProgress(0);
    void audio.play().catch(() => {
      // Only reset if this segment is still the active one — a later toggle may have superseded it.
      if (activeSegmentRef.current?.id === segment.id) stop();
    });
  };

  return {
    audioRef,
    isPlaying: (id: number) => activeId === id,
    progressFor: (id: number) => (activeId === id ? progress : 0),
    toggle,
    handleTimeUpdate,
    handleEnded,
    handleError,
  };
}
